/* ═══════════════════════════════════════════════════════════════
   ModuleTagsInput — multi-select TAGS picker for a module (migration
   056). Suggestions come from `collectTags` (most-used first); typing a
   tag that isn't suggested yet offers to add it. Enforces the same
   bounds the server does (`MAX_MODULE_TAGS`, `MAX_TAG_LEN`) with an
   inline message tied to the control.

   Composed from the kit `SearchableSelect` multi mode — never a second
   dropdown or a hand-rolled chip input.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { SearchableSelect } from './SearchableSelect';
import { validateTags } from '../../shared/module-categories';

export interface ModuleTagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  /** Most-used-first suggestions, e.g. from `collectTags(modules)`. */
  suggestions: Array<{ tag: string; count: number }>;
  disabled?: boolean;
  ariaLabel?: string;
  id?: string;
}

export function ModuleTagsInput({
  value,
  onChange,
  suggestions,
  disabled,
  ariaLabel,
  id,
}: ModuleTagsInputProps) {
  const [error, setError] = React.useState<string | null>(null);
  const errorId = id ? `${id}-error` : undefined;

  const options = React.useMemo(
    () => suggestions.map((s) => ({ value: s.tag, label: s.tag, hint: String(s.count) })),
    [suggestions]
  );

  const commit = (tags: string[]) => {
    const { tags: clean, errors } = validateTags(tags);
    setError(errors[0] || null);
    onChange(clean);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <SearchableSelect
        multiple
        value=""
        onChange={() => {}}
        values={value}
        onChangeMulti={commit}
        options={options}
        placeholder="No tags"
        searchPlaceholder="Search, or type a new tag"
        ariaLabel={ariaLabel || 'Tags'}
        describedBy={error ? errorId : undefined}
        disabled={disabled}
        creatable
        creatableLabel={(q) => `Create tag "${q}"`}
        onCreate={(q) => commit([...value, q])}
      />
      {error && (
        <p
          id={errorId}
          aria-live="polite"
          style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

export default ModuleTagsInput;
